// TMOS User Approval Service
// Date: December 19, 2025
// Purpose: Frontend service for user approval API calls

import api from './api';

export interface UserApprovalRequest {
  id: string;
  user_id: string;
  tenant_id: string;
  requested_company_name?: string;
  normalized_company_name?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'MERGED';
  requested_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
  review_notes?: string;
  assigned_role?: string;
  assigned_hierarchies?: Record<string, any>;
  assigned_position?: string;
  merged_to_tenant_id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  user_status?: string;
  reviewer_first_name?: string;
  reviewer_last_name?: string;
}

export interface ApprovalStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  merged?: number;
}

export interface DuplicateDetectionResult {
  normalized_name: string;
  duplicates: Array<{
    tenant_id: string;
    name: string;
    similarity: number;
  }>;
  has_duplicates: boolean;
}

export const userApprovalService = {
  // Get pending approval requests
  async getPendingApprovals(page: number = 1, limit: number = 20): Promise<{
    approvals: UserApprovalRequest[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const response = await api.get('/user-approvals/pending', {
      params: { page, limit }
    });
    return response.data;
  },

  // Approve user request
  async approveUser(
    id: string,
    data: {
      reviewNotes?: string;
      assignedRole?: string;
      assignedHierarchies?: Record<string, any>;
      assignedPosition?: string;
      hierarchyLocation?: { geo_id?: string; func_id?: string; bus_id?: string };
    }
  ): Promise<void> {
    await api.post(`/user-approvals/${id}/approve`, data);
  },

  // Reject user request
  async rejectUser(id: string, reviewNotes?: string): Promise<void> {
    await api.post(`/user-approvals/${id}/reject`, { reviewNotes });
  },

  // Merge user to existing tenant
  async mergeToTenant(id: string, mergeToTenantId: string, reviewNotes?: string): Promise<void> {
    await api.post(`/user-approvals/${id}/merge`, {
      mergeToTenantId,
      reviewNotes
    });
  },

  // Get approval statistics
  async getApprovalStats(): Promise<ApprovalStats> {
    const response = await api.get('/user-approvals/stats');
    return response.data.stats;
  },

  // Detect duplicate company names
  async detectDuplicates(companyName: string): Promise<DuplicateDetectionResult> {
    const response = await api.get('/user-approvals/duplicates', {
      params: { companyName }
    });
    return response.data;
  },

  // Get user's approval history
  async getUserApprovalHistory(userId: string): Promise<UserApprovalRequest[]> {
    const response = await api.get(`/user-approvals/user/${userId}/history`);
    return response.data.history || [];
  }
};

